import React from 'react';
import { Box, ButtonBase, InputBase, SxProps, Theme } from '@mui/material';
import { elliottTokens as t } from '../tokens';
import { MOBILE_SX } from '../../../../../../mobile/mobileApp';

// Same button rules as the voice controls: words only, orange gradient with
// white text when available, dark when not.
const BUTTON: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: '14px',
  py: '8px',
  borderRadius: '8px',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 'normal',
  flexShrink: 0,
  border: '1px solid transparent',
  '&.Mui-focusVisible': { outline: `2px solid ${t.brandLight}`, outlineOffset: '2px' },
  [MOBILE_SX]: { minHeight: 40, px: '12px' }
};
const PRIMARY: SxProps<Theme> = {
  color: t.textPrimary,
  background: `linear-gradient(90deg, ${t.brandLight} 0%, ${t.brandDark} 100%)`,
  '&:hover': { filter: 'brightness(1.08)' }
};
const SECONDARY: SxProps<Theme> = {
  bgcolor: t.surface,
  color: t.textPrimary,
  borderColor: t.borderContrast,
  '&:hover': { borderColor: t.textSecondary }
};
const DISABLED: SxProps<Theme> = {
  bgcolor: 'rgba(0,0,0,0.2)',
  color: t.borderContrast,
  boxShadow: `inset 0 0 0 1px ${t.borderDim}`,
  cursor: 'not-allowed'
};

type InputBarProps = {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  /** Hand the turn to the spoken conversation ("Speak answer"); hidden when absent. */
  onSwitchToVoice?: () => void;
  /** Label for that switch — 'Retry voice' after a mic denial. */
  switchToVoiceLabel?: string;
  disabled?: boolean;
  placeholder?: string;
};

const InputBar: React.FC<InputBarProps> = ({
  value,
  onChange,
  onSend,
  onSwitchToVoice,
  switchToVoiceLabel = 'Speak Answer',
  disabled = false,
  placeholder = 'Reply to Elliott'
}) => {
  const hasText = value.trim().length > 0;
  const canSend = hasText && !disabled;

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        maxWidth: 1146,
        mx: 'auto',
        px: '12px',
        py: '14px',
        [MOBILE_SX]: { gap: '8px', px: '10px', py: '8px' },
        borderRadius: `${t.radius}px`,
        bgcolor: t.surface,
        border: `1.5px solid ${disabled ? t.borderDim : t.brandLight}`,
        // Brandy outline + glow when active; a clean, muted field when disabled (no glow).
        boxShadow: disabled
          ? 'none'
          : '0px 0px 16px 0px rgba(244,119,35,0.5), inset 0px 0px 8px 0px rgba(247,148,32,0.25)'
      }}
    >
      {/* Attachment affordance intentionally hidden until attachments have a
          defined behavior (product) — a dead icon reads as broken. Re-enable by
          restoring the LuPaperclip block. */}

      <InputBase
        multiline
        maxRows={6}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (hasText) onSend();
          }
        }}
        placeholder={placeholder}
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          color: t.textPrimary,
          fontSize: t.bodySize, // match message copy (16px)
          fontWeight: 500,
          lineHeight: 'normal',
          // Keep the field transparent (browsers paint a light bg on disabled textareas) and
          // preserve pasted formatting (line breaks / spacing) in the editor.
          '& textarea, & input': { backgroundColor: 'transparent', whiteSpace: 'pre-wrap' },
          '& textarea::placeholder, & input::placeholder': { color: t.textSecondary, opacity: 1 },
          // A global stylesheet paints disabled textareas light; !important beats
          // it so the locked bar stays dark instead of showing a white field.
          '&.Mui-disabled, & .MuiInputBase-input.Mui-disabled, & textarea:disabled, & input:disabled': {
            backgroundColor: 'transparent !important',
            WebkitTextFillColor: t.textTertiary,
            boxShadow: 'none !important'
          }
        }}
      />

      {/* Words only, and the same Send answer as the voice controls. */}
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
        <ButtonBase
          onClick={canSend ? onSend : undefined}
          aria-disabled={!canSend}
          sx={{ ...BUTTON, ...(canSend ? PRIMARY : DISABLED) }}
        >
          Send
        </ButtonBase>
        {onSwitchToVoice && (
          <ButtonBase
            onClick={disabled ? undefined : onSwitchToVoice}
            aria-disabled={disabled}
            title="Answer Elliott out loud"
            sx={{ ...BUTTON, ...(disabled ? DISABLED : SECONDARY) }}
          >
            {switchToVoiceLabel}
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
};

export default InputBar;
